import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MetaMessageSilencerService } from '@app/api/interceptors/meta-message-silencer.service';
import { getAccountSubscription } from '@app/reducers/account/account-service.selectors';
import { UNLIMITED_QUOTA } from '@app/reducers/account/account-subscription.helper';
import { getDepartmentOptions } from '@app/reducers/orm/department/department.selectors';
import { getActiveEmployees } from '@app/reducers/orm/employee/employee.selectors';
import { getActiveLocations } from '@app/reducers/orm/location/location.service';
import { getActiveTeams } from '@app/reducers/orm/team/team.service';
import { quotaRemaining } from '@app/shared/subscription-plan/subscription-quota.directive';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { ButtonComponent } from '@sb/ui/button';
import { ToastService } from '@sb/ui/toast/toast.service';
import { QuotaType } from '@shiftbase-com/ts-shared/models';

import { HydrationConfig } from './dev-tools-hydration.models';
import { DevToolsHydrationService } from './dev-tools-hydration.service';
import { DevToolsRateLimiter } from './dev-tools-rate-limiter';
import { DevToolsScaffoldService } from './dev-tools-scaffold.service';
import { DevToolsSectionComponent } from './dev-tools-section.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-dev-tools-data',
  templateUrl: './dev-tools-data.component.html',
  imports: [ButtonComponent, FormsModule, DevToolsSectionComponent],
  providers: [DevToolsScaffoldService, DevToolsHydrationService, DevToolsRateLimiter],
})
export class DevToolsDataComponent {
  private store = inject(Store<AppState>);
  private toastService = inject(ToastService);
  private scaffoldService = inject(DevToolsScaffoldService);

  #hydrationService = inject(DevToolsHydrationService);
  #metaMessageSilencer = inject(MetaMessageSilencerService);

  isLoading = signal(false);
  loadingMessage = signal('');
  Infinity = Infinity;
  UNLIMITED_QUOTA = UNLIMITED_QUOTA;

  entityCount = signal(5);
  generateLocations = signal(false);
  generateDepartments = signal(false);
  generateTeams = signal(false);
  generateEmployees = signal(false);
  generateShiftTemplates = signal(false);
  generateScheduledShifts = signal(false);
  generateTimesheets = signal(false);
  generateAbsences = signal(false);

  private subscription = this.store.selectSignal(getAccountSubscription);
  locations = this.store.selectSignal(getActiveLocations);
  private departments = this.store.selectSignal(
    getDepartmentOptions({ userId: 'me', permissions: ['Manage account'] }, true),
  );
  private teams = this.store.selectSignal(getActiveTeams);
  private employees = this.store.selectSignal(getActiveEmployees);

  quotaInfo = computed(() => {
    const sub = this.subscription();
    if (!sub) return null;

    const departmentsRemaining = quotaRemaining(QuotaType.DEPARTMENTS, sub);
    const teamsRemaining = quotaRemaining(QuotaType.TEAMS, sub);
    const employeesRemaining = quotaRemaining(QuotaType.SEATS, sub);

    const departmentsLimit = sub.plan?.quotas?.[QuotaType.DEPARTMENTS]?.limit ?? Infinity;
    const teamsLimit = sub.plan?.quotas?.[QuotaType.TEAMS]?.limit ?? Infinity;
    const employeesLimit = sub.plan?.quotas?.[QuotaType.SEATS]?.limit ?? Infinity;

    return {
      departments: {
        limit: departmentsLimit,
        remaining: departmentsRemaining,
        current:
          departmentsLimit === Infinity ? (this.departments()?.length ?? 0) : departmentsLimit - departmentsRemaining,
      },
      teams: {
        limit: teamsLimit,
        remaining: teamsRemaining,
        current: teamsLimit === Infinity ? (this.teams()?.length ?? 0) : teamsLimit - teamsRemaining,
      },
      employees: {
        limit: employeesLimit,
        remaining: employeesRemaining,
        current: employeesLimit === Infinity ? (this.employees()?.length ?? 0) : employeesLimit - employeesRemaining,
      },
    };
  });

  canAddDepartment = () => {
    const info = this.quotaInfo();
    return info && info.departments.remaining > 0;
  };

  canAddTeams = (count: number) => {
    const info = this.quotaInfo();
    return info && info.teams.remaining >= count;
  };

  canAddEmployees = (count: number) => {
    const info = this.quotaInfo();
    return info && info.employees.remaining >= count;
  };

  scaffoldProject(size: 'small' | 'medium' | 'large' | 'megaCorp'): void {
    const info = this.quotaInfo();
    if (!info) return;

    const projectConfig = {
      small: {
        locations: 1,
        departments: 2,
        teams: 3,
        employees: 10,
      },
      medium: {
        locations: 2,
        departments: 5,
        teams: 10,
        employees: 20,
      },
      large: {
        locations: 3,
        departments: 10,
        teams: 20,
        employees: 50,
      },
      megaCorp: {
        locations: 5,
        departments: 20,
        teams: 50,
        employees: 100,
      },
    };

    const locationsToCreate = Math.max(0, projectConfig[size].locations - this.locations().length);
    const departmentsToCreate = Math.min(
      info.departments.remaining,
      Math.max(0, projectConfig[size].departments - this.departments().length),
    );
    const teamsToCreate = Math.min(info.teams.remaining, Math.max(0, projectConfig[size].teams - this.teams().length));
    const employeesToCreate = Math.min(
      info.employees.remaining,
      Math.max(0, projectConfig[size].employees - this.employees().length),
    );

    if (locationsToCreate === 0 && departmentsToCreate === 0 && teamsToCreate === 0 && employeesToCreate === 0) {
      this.toastService.openInfo('Project already meets or exceeds target configuration');
      return;
    }

    this.isLoading.set(true);

    const config = {
      locationsToCreate,
      departmentsToCreate,
      teamsToCreate,
      employeesToCreate,
      existingLocations: this.locations(),
      existingDepartments: this.departments(),
      existingTeams: this.teams(),
    };

    this.#metaMessageSilencer
      .runSilenced(() => this.scaffoldService.scaffold(config, (message) => this.loadingMessage.set(message)))
      .then(() => {
        this.isLoading.set(false);
        this.loadingMessage.set('');
      })
      .catch(() => {
        this.isLoading.set(false);
        this.loadingMessage.set('');
      });
  }

  generateEntities(): void {
    const count = this.entityCount();
    const genLocations = this.generateLocations();
    const genDepartments = this.generateDepartments();
    const genTeams = this.generateTeams();
    const genEmployees = this.generateEmployees();
    const genShiftTemplates = this.generateShiftTemplates();
    const genScheduledShifts = this.generateScheduledShifts();
    const genTimesheets = this.generateTimesheets();
    const genAbsences = this.generateAbsences();

    const generatesStructure = genLocations || genDepartments || genTeams || genEmployees;
    const generatesActivity = genShiftTemplates || genScheduledShifts || genTimesheets || genAbsences;

    // Validate that at least one entity type is selected
    if (!generatesStructure && !generatesActivity) {
      this.toastService.openDanger('Please enable at least one entity type');
      return;
    }

    this.isLoading.set(true);

    const scaffoldConfig = {
      locationsToCreate: genLocations ? count : 0,
      departmentsToCreate: genDepartments ? count : 0,
      teamsToCreate: genTeams ? count : 0,
      employeesToCreate: genEmployees ? count : 0,
      existingLocations: this.locations(),
      existingDepartments: this.departments(),
      existingTeams: this.teams(),
    };

    const hydrationConfig: HydrationConfig = {
      shiftTemplatesToCreate: genShiftTemplates ? count : 0,
      scheduledShiftsToCreate: genScheduledShifts ? count : 0,
      timesheetsToCreate: genTimesheets ? count : 0,
      absencesToCreate: genAbsences ? count : 0,
    };

    const onProgress = (message: string) => this.loadingMessage.set(message);

    // Silence the per-request success toasts — the dev tools show their own summary
    this.#metaMessageSilencer
      .runSilenced(async () => {
        if (generatesStructure) {
          await this.scaffoldService.scaffold(scaffoldConfig, onProgress);
        }

        if (generatesActivity) {
          await this.#hydrationService.hydrate(hydrationConfig, onProgress);
        }
      })
      .then(() => {
        this.isLoading.set(false);
        this.loadingMessage.set('');
      })
      .catch((error) => {
        this.toastService.openDanger(error?.message || 'Failed to generate entities');
        this.isLoading.set(false);
        this.loadingMessage.set('');
      });
  }
}
